import { Button, PdfViewer, Spinner } from '@eureca/ui';
import type { SharedDocumentKind } from './shared-document-query';
import { TriangleAlertIcon } from './icons';

/** Estados da visualização externa — todos distintos e explícitos (RN-42). */
export type SharedDocumentState = 'loading' | 'ready' | 'expired' | 'error';

export interface SharedDocumentPageProps {
  kind: SharedDocumentKind;
  /** Nome (social pref.) do candidato — null em token antigo (RN-31). */
  candidateName: string | null;
  candidateEmail: string | null;

  state: SharedDocumentState;
  /** Object URL do PDF baixado — presente só em `state === 'ready'`. */
  documentUrl: string | null;
  /** Re-tenta o download em erro de API (5xx/rede) — NUNCA no expirado. */
  onRetry: () => void;

  /** Aviso de bloqueio (RN-35) — estado no container. */
  isNoticeOpen: boolean;
  onNoticeOpenChange: (open: boolean) => void;
  /** Tentativa bloqueada (contextmenu na área do documento — EXT-006). */
  onBlockedAttempt: () => void;
  /**
   * RN-33 — o PDF.js falhou ao renderizar (offline/worker): o container troca
   * para o estado de erro. NUNCA o fallback "Abrir PDF" do PdfViewer, que
   * entregaria o blob ao viewer nativo (com download/print).
   */
  onPdfLoadError: () => void;
}

const KIND_LABEL: Record<SharedDocumentKind, string> = {
  resume: 'Currículo',
  attestation: 'Laudo',
};

/** RN-37 — copy do empty state por tipo de documento. */
const EXPIRED_COPY: Record<SharedDocumentKind, string> = {
  resume:
    'O link de acesso a esse currículo expirou. Peça um novo link a quem te enviou o currículo.',
  attestation: 'O link de acesso a esse laudo expirou. Peça um novo link a quem te enviou o laudo.',
};

/**
 * RN-34 — `beforeprint` não cancela a impressão, então o CSS de print esconde
 * TUDO (inclusive portais de dialog sob o `body`) e sai página em branco. O
 * `<style>` é montado junto com a página e desmonta com ela (escopo da rota).
 */
const PRINT_BLOCK_CSS = '@media print { body * { display: none !important; } }';

/**
 * Página PÚBLICA de visualização de currículo/laudo compartilhado (EU-594,
 * comentário 23123) — Figma 15616:39596 (currículo), 15616:39604 (laudo),
 * 15617:39629/39635 (aviso de bloqueio), 15616:39602/39610 (expirado).
 *
 * - Válido: header `{Currículo|Laudo}: {nome} | {e-mail}` SEM menu de
 *   consultor e SEM Compartilhar/Voltar (RN-30/31); PDF na área toda.
 * - Expirado/inválido: empty state SEM header e SEM documento (RN-38/40),
 *   sem CTA (RN-39).
 * - Aviso de bloqueio: Dialog com copy exata e ação Fechar (RN-35); fechar
 *   mantém o documento visível (EXT-007).
 *
 * Puro: sem hooks, sem router — listeners de teclado/print vivem no container.
 */
export function SharedDocumentPage({
  kind,
  candidateName,
  candidateEmail,
  state,
  documentUrl,
  onRetry,
  isNoticeOpen,
  onNoticeOpenChange,
  onBlockedAttempt,
  onPdfLoadError,
}: SharedDocumentPageProps) {
  const identity = [candidateName, candidateEmail].filter(Boolean).join(' | ');

  if (state === 'expired') {
    // RN-38 — nem header, nem documento, nem CTA (RN-39).
    return (
      <div
        className="flex h-dvh flex-col items-center justify-center gap-6 bg-background px-6 text-center"
        data-testid="shared-document-expired"
      >
        <style>{PRINT_BLOCK_CSS}</style>
        <div className="flex size-14 items-center justify-center rounded-full bg-muted">
          <TriangleAlertIcon className="size-6 text-foreground" />
        </div>
        <div className="flex max-w-md flex-col gap-2">
          <h1 className="text-lg font-semibold text-foreground">Link expirado</h1>
          <p className="text-sm text-muted-foreground">{EXPIRED_COPY[kind]}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-dvh flex-col bg-background" data-testid="shared-document-page">
      <style>{PRINT_BLOCK_CSS}</style>

      {state === 'ready' && (
        <header className="z-10 flex flex-none flex-row items-center border-b border-border bg-card px-8 py-5 shadow-sm md:px-16">
          <p
            className="min-w-0 truncate text-sm text-foreground"
            data-testid="shared-document-title"
          >
            <span className="font-semibold">
              {KIND_LABEL[kind]}
              {identity ? ': ' : ''}
            </span>
            {identity}
          </p>
        </header>
      )}

      {/* Área do documento — select-none + contextmenu bloqueado (RN-33). */}
      <div
        className="relative flex min-h-0 flex-1 select-none flex-col bg-neutral-300"
        onContextMenu={(event) => {
          event.preventDefault();
          onBlockedAttempt();
        }}
        data-testid="shared-document-area"
      >
        {state === 'loading' && (
          <div
            className="flex flex-1 items-center justify-center"
            data-testid="shared-document-loading"
          >
            <Spinner className="size-8 text-muted-foreground" />
          </div>
        )}

        {state === 'error' && (
          <div
            className="flex flex-1 flex-col items-center justify-center gap-4 px-6 text-center"
            data-testid="shared-document-error"
          >
            <p className="text-sm text-foreground">
              Não foi possível carregar o documento. Tente novamente.
            </p>
            <Button
              type="button"
              variant="outline"
              onClick={onRetry}
              data-testid="shared-document-retry"
            >
              Tentar novamente
            </Button>
          </div>
        )}

        {state === 'ready' && documentUrl && (
          <PdfViewer
            url={documentUrl}
            title={`${KIND_LABEL[kind]} compartilhado`}
            className="min-h-0 flex-1 bg-neutral-300"
            // RN-33 — sem o fallback "Abrir PDF" (abriria o blob no viewer
            // nativo, com download/print). Falha de render vira erro nosso.
            fallback="none"
            onLoadError={onPdfLoadError}
            data-testid="shared-document-pdf"
          />
        )}

        {/* Aviso de bloqueio (RN-35) — barra flutuante do Figma (15617:39629):
            não-modal, o documento continua visível atrás (EXT-007). */}
        {isNoticeOpen && (
          <div
            role="status"
            className="absolute inset-x-0 bottom-10 z-20 flex justify-center px-4"
            data-testid="shared-document-block-dialog"
          >
            <div className="flex w-full max-w-md flex-row items-center gap-4 rounded-lg bg-card py-2 pl-4 pr-2 shadow-lg">
              <p className="min-w-0 flex-1 text-sm text-foreground">
                Não é possível baixar nem imprimir este arquivo.
              </p>
              <Button
                type="button"
                size="sm"
                onClick={() => onNoticeOpenChange(false)}
                data-testid="shared-document-block-close"
              >
                Fechar
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
